import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionSummary, Box, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';

import { FHTooltip, FSwitch, FHInputField, FHConfirmDialog } from '../../../../base';
import { goalActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../../helpers';
import { FORTA_BLUE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS, GoalTypes, iconButtonStyle } from '../common/constants/general';
import EditOutlinedIcon from '../../../../svgs/EditOutlinedIcon';
import TrashIcon from '../../../../svgs/TrashIcon';

import type { ITreatmentPlanGoal } from '../../../../../utils/types';
import type { RootState } from '../../../../../store';
import type { ChangeEvent } from 'react';

interface GoalSummaryProps {
  index: number;
  goal: ITreatmentPlanGoal;
  submitting: boolean;
  onUpdateField: (key: string, value: any) => void;
  onDelete: () => void;
}

function GoalSummary(props: GoalSummaryProps) {
  const { index, goal, submitting, onUpdateField, onDelete } = props;
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isTpEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { skillAcquisitionGoals, caregiverGoals, behaviorReductionGoals } = useSelector(
    (state: RootState) => state.goals,
  );
  const [activeStatus, setActiveStatus] = useState<boolean>(true);
  const [reason, setReason] = useState<string>('');
  const [linkedGoalNames, setLinkedGoalNames] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);

  const [errorDialogText, setErrorDialogText] = useState<string>('');

  useEffect(() => {
    if (!goal) return;

    setActiveStatus(goal?.is_active);
    const linkedGoals = [];
    if (goal.linked_goals?.behavior_reduction) {
      linkedGoals.push(...goal.linked_goals.behavior_reduction);
    }
    if (goal.linked_goals?.skill_acquisition) {
      linkedGoals.push(...goal.linked_goals.skill_acquisition);
    }
    if (linkedGoals && linkedGoals.length >= 0) {
      setLinkedGoalNames(
        linkedGoals
          .map((linkedGoal) =>
            capitalizeFirstLetter(linkedGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ?? ''),
          )
          .join(', '),
      );
    }
  }, [goal]);

  const handleSwitchToggle = (checked: boolean) => {
    if (!goal) return;
    if (goal?.goal_type === GoalTypes.SkillAcquisition) {
      const linkedBehaviorReductionGoals = goal?.linked_goals?.behavior_reduction;
      let atleastOneBRGoalDoesNotHaveLinkedSAGoalAfterCurrentGoalIsDeactivated = false;

      linkedBehaviorReductionGoals?.forEach((linkedBRGoal) => {
        const brGoal = behaviorReductionGoals.find((brGoal) => brGoal?.id === linkedBRGoal?.id);
        const doesBRGoalHaveAnotherActiveSAGoal = brGoal?.linked_goals?.skill_acquisition?.find(
          (linkedSAGoal) => {
            const saGoal = skillAcquisitionGoals.find((saGoal) => saGoal?.id === linkedSAGoal?.id);
            return saGoal?.id !== goal?.id && saGoal?.is_active;
          },
        );
        if (!doesBRGoalHaveAnotherActiveSAGoal) {
          setErrorDialogText(
            `The behavior reduction goal ("${brGoal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}") needs to be linked to atleast one other active skill aquisition goal before you can deactivate the current goal ("${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}").`,
          );
          setShowErrorDialog(true);
          atleastOneBRGoalDoesNotHaveLinkedSAGoalAfterCurrentGoalIsDeactivated = true;
          return;
        }
      });
      if (atleastOneBRGoalDoesNotHaveLinkedSAGoalAfterCurrentGoalIsDeactivated) {
        return;
      }
    }

    if (goal?.goal_type === GoalTypes.BehaviorReduction) {
      const linkedSkillAcquisitionGoals = goal?.linked_goals?.skill_acquisition;
      let atleastOneSAGoalDoesNotHaveLinkedBRGoalAfterCurrentGoalIsDeactivated = false;

      linkedSkillAcquisitionGoals?.forEach((linkedSAGoal) => {
        const saGoal = skillAcquisitionGoals.find((saGoal) => saGoal?.id === linkedSAGoal?.id);
        const doesSAGoalHaveAnotherActiveBRGoal = saGoal?.linked_goals?.behavior_reduction?.find(
          (linkedBRGoal) => {
            const brGoal = behaviorReductionGoals.find((brGoal) => brGoal?.id === linkedBRGoal?.id);
            return brGoal?.id !== goal?.id && brGoal?.is_active;
          },
        );
        if (!doesSAGoalHaveAnotherActiveBRGoal) {
          setErrorDialogText(
            `The skill acquisition goal ("${saGoal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}") needs to be unlinked from the current behavior reduction goal before the current goal ("${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}") can be deactivated.`,
          );
          setShowErrorDialog(true);
          atleastOneSAGoalDoesNotHaveLinkedBRGoalAfterCurrentGoalIsDeactivated = true;
          return;
        }
      });
      if (atleastOneSAGoalDoesNotHaveLinkedBRGoalAfterCurrentGoalIsDeactivated) {
        return;
      }
    }

    const activeSkillAcquisitionGoals = skillAcquisitionGoals?.filter(
      (sg: ITreatmentPlanGoal) => sg.is_active && sg.id && sg.id !== goal.id,
    );
    const activeCaregiverGoals = caregiverGoals?.filter(
      (cg: ITreatmentPlanGoal) => cg.is_active && cg.id && cg.id !== goal.id,
    );
    if (!checked) {
      if (
        goal.goal_type === GoalTypes.SkillAcquisition &&
        activeSkillAcquisitionGoals.length === 0
      ) {
        toast.error('TP should have at least one active goal in skill acquisition.');
        return;
      }
      if (goal.goal_type === GoalTypes.Caregiver && activeCaregiverGoals.length === 0) {
        toast.error('TP should have at least one active goal in parent/caregiver.');
        return;
      }
    }

    setActiveStatus(checked);
    setReason('');
  };

  const handleUpdateGoalActivation = async () => {
    if (!treatmentPlan || !goal) return;

    if (reason.trim().length < 2) {
      toast.error('Reason requires at least 2 characters.');
      return;
    }

    if (!activeStatus) {
      const res = await dispatch(
        goalActions.deactivateGoal({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
      toast.success('Goal deactivated.');
    } else {
      const res = await dispatch(
        goalActions.activateGoal({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
      toast.success('Goal activated.');
    }

    onUpdateField('is_active', {
      activeStatus,
      reason,
    });
  };

  return (
    <AccordionSummary
      expandIcon={
        <ExpandMoreIcon data-testid={`expand_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`} />
      }
      sx={{
        padding: '16px 24px',
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent',
        },
        '& .MuiAccordionSummary-content': {
          display: 'flex',
          flexDirection: 'column',
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
          position: 'absolute',
          right: 10,
          top: linkedGoalNames ? 30 : 24,
        },
      }}
      onClick={() => onUpdateField('expanded', !goal?.expanded)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column', justifyContent: 'center' }}
        >
          <Box sx={{ display: 'flex' }}>
            <Typography
              variant="h4"
              color={FORTA_BLUE.primary}
              fontWeight={500}
              lineHeight={'34px'}
              height={34}
              mr={2}
            >
              Goal {index + 1}:{' '}
              {capitalizeFirstLetter(goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || '')}
            </Typography>
          </Box>
          {!goal?.is_editing && linkedGoalNames && (
            <Box sx={{ display: 'flex' }}>
              <LinkIcon color={'secondary'} sx={{ mr: 1 }} />
              <Typography textAlign={'left'}>{linkedGoalNames}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 5 }}>
          <IconButton
            data-testid={`delete_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
            sx={iconButtonStyle}
            hidden={!isTpEditable}
            disabled={submitting}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <TrashIcon color={FORTA_BLUE.primary} />
          </IconButton>
          <IconButton
            data-testid={`edit_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
            sx={{ ...iconButtonStyle, opacity: goal?.is_editing ? 0.3 : 1 }}
            hidden={!isTpEditable}
            disabled={submitting}
            onClick={(e) => {
              e.stopPropagation();
              onUpdateField('is_editing', !goal?.is_editing);
            }}
          >
            <EditOutlinedIcon color={FORTA_BLUE.primary} />
          </IconButton>
          <FHTooltip
            title="Activate"
            followCursor
            disableFocusListener
            disableTouchListener
            placement="bottom-start"
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <FSwitch
                data-testid={`goalSummaryActiveStatus_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}_${activeStatus}`}
                colorTheme={Themes.Primary}
                coloredBackground
                checked={activeStatus}
                disabled={!isTpEditable || !Object.hasOwn(goal, 'id')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleSwitchToggle(e.target.checked);
                }}
              />
            </Box>
          </FHTooltip>
        </Box>
      </Box>
      {goal?.is_active !== activeStatus && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
            Reason for status change
          </Typography>
          <FHInputField
            data-testid={`GoalStatusReasonChangeTextField_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
            sx={{ flex: 1 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            coloredBackground
            value={reason}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
            onBlur={() => handleUpdateGoalActivation()}
            error={reason?.length < 2 ? true : false}
          />
        </Box>
      )}
      {!goal?.is_editing && !goal?.is_active && goal?.is_active === activeStatus && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
            Reason for deactivation
          </Typography>
          <Typography align="left">{goal?.reason || ''}</Typography>
        </Box>
      )}
      <FHConfirmDialog
        open={showErrorDialog}
        colorTheme={Themes.Primary}
        content={errorDialogText}
        submittext={'Okay'}
        hideCancelButton={true}
        onClose={() => setShowErrorDialog(false)}
        onSubmit={() => {
          setShowErrorDialog(false);
        }}
        dialogHeight={420}
      />
    </AccordionSummary>
  );
}

export default GoalSummary;
